import { Injectable, Signal, computed, effect, inject, OnDestroy } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { CallState, CallSession } from './call-types';
import { TelnyxWebRTCService } from '../telnyx-webrtc.service';
import { CallLifecycleService } from './call-lifecycle.service';
import { CallSessionService } from './call-session.service';
import { CallEventsService } from './call-events.service';
import { CallErrorHandler } from './call-error-handler.service';
import { EnhancedCallModalService } from '../enhanced-call-modal.service';
import { LeadLookupService } from '../lead-lookup.service';
import { CallApiService } from './call-api.service';
import { MatDialog } from '@angular/material/dialog';
import { GlobalCallPanelService } from '../global-call-panel.service';
import { GlobalCallContextService } from '../global-call-context.service';
import { LeadAlertCallComponent, LeadAlertCallData } from '../../shared/lead-alert-call/lead-alert-call.component';

/**
 * TelnyxIntegrationService
 *
 * Coordinates WebRTC events between TelnyxWebRTCService and the call management system.
 * Acts as an adapter/coordinator without duplicating functionality.
 *
 * Responsibilities:
 * - Route WebRTC events to appropriate call services
 * - Sync state between WebRTC SDK and application state
 * - Coordinate call operations through TelnyxWebRTCService
 * - Handle minimum ringback duration logic
 * - Map WebRTC states to application states
 *
 * Target: ~350 lines (under 400 line requirement)
 */

// WebRTC event types from Telnyx SDK
export interface TelnyxEvent { event: string; data: any; }

// State mapping from Telnyx to application
const STATE_MAPPING: Record<string, CallState> = {
  'idle': 'idle', 'dialing': 'dialing', 'trying': 'dialing', 'ringing': 'ringing',
  'connecting': 'connecting', 'connected': 'connected', 'active': 'connected',
  'held': 'held', 'ending': 'ending', 'ended': 'ended', 'failed': 'failed'
};

// Terminal states that should not be overridden
const TERMINAL_STATES: CallState[] = ['ended', 'failed', 'post-call', 'idle'];

// Minimum ringback duration before transitioning to connected (1.5 seconds)
const MIN_RINGBACK_DURATION_MS = 1500;

@Injectable({ providedIn: 'root' })
export class TelnyxIntegrationService implements OnDestroy {
  // Dependencies
  private telnyxService = inject(TelnyxWebRTCService);
  private lifecycle = inject(CallLifecycleService);
  private sessionService = inject(CallSessionService);
  private eventsService = inject(CallEventsService);
  private errorHandler = inject(CallErrorHandler);
  private modalService = inject(EnhancedCallModalService);
  private leadLookupService = inject(LeadLookupService);
  private callApiService = inject(CallApiService);
  private dialog = inject(MatDialog);
  private globalCallPanelService = inject(GlobalCallPanelService);
  private globalCallContext = inject(GlobalCallContextService);

  // WebRTC event stream
  private webrtcEventsSubject = new Subject<TelnyxEvent>();
  readonly webrtcEvents$ = this.webrtcEventsSubject.asObservable();

  // State sync control flags
  private preventWebRTCSync = false;
  private isTerminating = false;
  private cleanupTimer: ReturnType<typeof setTimeout> | null = null;

  // Modal tracking
  private openModalCallIds = new Set<string>();

  // Public signals for connection status
  readonly connected: Signal<boolean>;
  readonly connectionStatus: Signal<string>;

  constructor() {
    // Expose Telnyx connection state as signals
    this.connected = this.telnyxService.connected;
    this.connectionStatus = computed(() => this.telnyxService.connectionStatus());

    // Setup integration
    this.setupEventListeners();
    this.setupStateSync();
    this.setupConnectionMonitoring();
    this.setupIncomingCallModalHandler();
  }

  ngOnDestroy(): void { this.cleanup(); }

  /** Initialize integration (called after services are ready) */
  initialize(): void {
    // Integration is set up in constructor
    // This method exists for explicit initialization if needed
  }

  /** Cleanup integration */
  cleanup(): void {
    if (this.cleanupTimer) {
      clearTimeout(this.cleanupTimer);
      this.cleanupTimer = null;
    }
    this.webrtcEventsSubject.complete();
  }

  // ============================================================================
  // Call Operations (delegate to TelnyxWebRTCService)
  // ============================================================================

  /** Make an outbound call through Telnyx */
  async makeCall(phoneNumber: string, leadId?: number): Promise<any> {
    try {
      return await this.telnyxService.makeCall(phoneNumber, leadId);
    } catch (error) {
      const processedError = this.errorHandler.processError(error, 'CALL_INITIATION_FAILED');
      this.eventsService.emitError(processedError);
      throw error;
    }
  }

  /** Answer incoming call through Telnyx */
  answerCall(): boolean {
    const success = this.telnyxService.answerCall();
    if (!success) {
      const error = this.errorHandler.processError(new Error('Failed to answer call'), 'ANSWER_FAILED');
      this.eventsService.emitError(error);
    }
    return success;
  }

  /** Hangup current call through Telnyx */
  hangupCall(): void { this.telnyxService.hangupCall(); }

  /** Toggle hold state through Telnyx */
  toggleHold(): void { this.telnyxService.toggleHold(); }

  // ============================================================================
  // Event Listeners Setup
  // ============================================================================

  /** Setup listeners for Telnyx WebRTC events */
  private setupEventListeners(): void {
    // Subscribe to Telnyx call events
    this.telnyxService.getCallEvents().subscribe(event => {
      this.handleTelnyxCallEvent(event);
      this.webrtcEventsSubject.next(event);
    });

    // Subscribe to Telnyx errors
    this.telnyxService.getErrors().subscribe(error => {
      const processedError = this.errorHandler.processError(error, 'TELNYX_ERROR');
      this.eventsService.emitError(processedError);
    });
  }

  /** Setup state synchronization between Telnyx and app state */
  private setupStateSync(): void {
    // Monitor Telnyx call state changes with race condition prevention
    effect(() => {
      const telnyxState = this.telnyxService.callState();
      const telnyxCall = this.telnyxService.currentCall();
      if (telnyxState && telnyxCall && !this.preventWebRTCSync) {
        this.syncWithTelnyxState(telnyxState, telnyxCall);
      }
    }, { allowSignalWrites: true });
  }

  /** Setup connection monitoring for disconnection handling */
  private setupConnectionMonitoring(): void {
    effect(() => {
      const telnyxConnected = this.telnyxService.connected();
      const currentState = this.lifecycle.currentState();
      // If WebRTC disconnects while in active call, handle cleanup
      if (!telnyxConnected && !TERMINAL_STATES.includes(currentState)) {
        this.handleConnectionLoss();
      }
    }, { allowSignalWrites: true });
  }

  /**
   * Setup incoming call modal handler
   * Subscribes to CallEventsService.callStarted$ for incoming calls
   * and opens the modal AFTER the call is registered
   */
  private setupIncomingCallModalHandler(): void {
    this.eventsService.onCallStarted()
      .pipe(filter(session => session.direction === 'inbound'))
      .subscribe(session => {
        console.log('[TelnyxIntegration] Incoming call started, opening modal', session);
        this.openIncomingCallModal(session);
      });
  }

  /**
   * Open incoming call modal with full session data
   */
  private openIncomingCallModal(session: CallSession): void {
    // Prevent duplicate modals for the same call
    if (this.openModalCallIds.has(session.telnyxCallId || session.id)) {
      console.warn('[TelnyxIntegration] Modal already open for call', session.telnyxCallId);
      return;
    }

    // Track this modal
    this.openModalCallIds.add(session.telnyxCallId || session.id);

    // Extract leadInfo and dbCallId from session if available
    const leadInfo = (session as any).leadInfo;
    const dbCallId = (session as any).dbCallId;
    const isLeadAlert = (session as any).isLeadAlert;
    const alertSessionId = (session as any).alertSessionId;

    console.log('[TelnyxIntegration] Opening modal for incoming call', {
      phoneNumber: session.phoneNumber,
      telnyxCallId: session.telnyxCallId,
      sessionId: session.id,
      alertSessionId: alertSessionId,
      hasLeadInfo: !!leadInfo,
      dbCallId: dbCallId,
      isLeadAlert: isLeadAlert
    });

    // Check if this is a lead alert call
    if (isLeadAlert && alertSessionId) {
      this.openLeadAlertModal({
        call: session,
        leadInfo: leadInfo,
        sessionId: alertSessionId,  // Use backend session ID, not local session ID
        phoneNumber: session.phoneNumber,
        isLeadAlert: isLeadAlert
      });
      return;
    }

    // Otherwise continue with regular incoming call modal
    this.modalService.showIncomingCall({
      phoneNumber: session.phoneNumber,
      callerName: leadInfo?.name,
      leadId: leadInfo?.id || session.leadId,
      callControlId: session.telnyxCallId,
      fromAgent: false,
      leadInfo: leadInfo,
      dbCallId: dbCallId  // Pass dbCallId for disposition API
    }).subscribe({
      next: (result) => {
        console.log('[TelnyxIntegration] Modal closed', result);
        this.openModalCallIds.delete(session.telnyxCallId || session.id);
      },
      error: (error) => {
        console.error('[TelnyxIntegration] Modal error', error);
        this.openModalCallIds.delete(session.telnyxCallId || session.id);
      }
    });
  }

  /**
   * Open lead alert call modal
   */
  private openLeadAlertModal(data: any): void {
    const dialogData: LeadAlertCallData = {
      call: data.call,
      leadInfo: data.leadInfo,
      sessionId: data.sessionId,
      phoneNumber: data.phoneNumber,
    };

    const dialogRef = this.dialog.open(LeadAlertCallComponent, {
      width: '420px',
      maxHeight: '90vh',
      disableClose: true,
      panelClass: 'lead-alert-dialog',
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe(result => {
      this.openModalCallIds.delete(data.call.telnyxCallId || data.sessionId);

      if (result?.reason === 'bridged') {
        // Capture inbound context with lead info before transitioning
        const session = {
          id: data.sessionId,
          direction: 'inbound' as const,
          phoneNumber: data.phoneNumber,
          startTime: new Date(),
          state: 'connected' as const,
          telnyxCallId: data.call?.telnyxCallId || data.sessionId,
          leadId: data.leadInfo?.id || null
        };

        const callerInfo = {
          id: data.leadInfo?.id,
          name: data.leadInfo?.name || 'Unknown Caller',
          phone: data.phoneNumber,
          campus: data.leadInfo?.campusName,
          program: data.leadInfo?.programName,
          owner: data.leadInfo?.ownerName
        };

        this.globalCallContext.captureInboundContext(session as any, callerInfo);
        this.globalCallPanelService?.transitionFromInboundModal();
      }
    });
  }

  // ============================================================================
  // WebRTC Event Routing
  // ============================================================================

  /**
   * Route Telnyx call events to appropriate handlers
   * Based on call-state.service.ts lines 1551-1627
   */
  private handleTelnyxCallEvent(event: { event: string, data: any }): void {
    const { event: eventType, data } = event;
    const handlers: Record<string, () => void> = {
      'incoming-call': () => this.handleIncomingCall(data),
      'call-trying': () => this.transitionIfActive('dialing'),
      'call-progress': () => this.transitionIfActive('connecting'),
      'call-ringing': () => this.transitionIfActive('ringing'),
      'call-answered': () => this.handleCallAnswered(data),
      'call-held': () => this.transitionIfActive('held'),
      'call-unheld': () => this.transitionIfActive('connected'),
      'call-ended': () => this.handleCallEnded(data),
      'call-failed': () => this.handleCallTerminal(data, 'failed', 'Failed: '),
      'call-busy': () => this.handleCallTerminal(data, 'busy', 'Busy signal received'),
      'call-timeout': () => this.handleCallTerminal(data, 'no-answer', 'No answer'),
      'autoplay_blocked': () => this.emitError('AUTOPLAY_BLOCKED', 'Click anywhere to enable call audio', data),
      'audio_routing_failed': () => this.emitError('AUDIO_ROUTING_FAILED', 'Call audio not available. Check your audio settings.', data)
    };
    handlers[eventType]?.();
  }

  // ============================================================================
  // Event Handlers
  // ============================================================================

  private handleIncomingCall(data: any): void {
    const { call, telnyxCallControlId, telnyxLegId, phoneNumber, leadInfo, dbCallId, isLeadAlert, sessionId } = data;
    const telnyxCallId = call?.id || data?.call_id;

    // Use the phone number passed from telnyx-webrtc.service or extract from call object
    const callerPhoneNumber = phoneNumber ||
                              call?.callerIdNumber ||
                              call?.from ||
                              data?.from ||
                              data?.caller_id_number ||
                              'Unknown';

    console.log('[TelnyxIntegration] Incoming call detected:', {
      telnyxCallControlId,
      telnyxLegId,
      telnyxCallId,
      phoneNumber: callerPhoneNumber,
      hasLeadInfo: !!leadInfo,
      dbCallId,
    });

    // ENHANCED: Check if this call is already registered to prevent duplicate registration
    // Check 1: Look for existing session by Telnyx ID
    const existingSession = this.sessionService.findByTelnyxId(telnyxCallId);
    if (existingSession) {
      console.log('[TelnyxIntegration] Call already registered, skipping duplicate');
      return;  // Skip duplicate registration
    }

    // Check 2: If already in ringing state with an active call, skip registration
    const currentState = this.lifecycle.currentState();
    const activeSessions = this.sessionService.getActiveSessions();
    if (currentState === 'ringing' && activeSessions.length > 0) {
      console.log('[TelnyxIntegration] Already in ringing state, skipping duplicate');
      return;  // Skip duplicate registration
    }

    // Register the inbound call with lead info from client_state (if available)
    try {
      this.lifecycle.registerInbound({
        phoneNumber: callerPhoneNumber,
        telnyxCallId: telnyxCallId,
        leadId: leadInfo?.id || data.leadId,
        callerName: leadInfo?.name || data.callerName,
        leadInfo: leadInfo, // Pass full leadInfo to session
        dbCallId: dbCallId,  // Pass dbCallId from custom headers for disposition API
        isLeadAlert: isLeadAlert,  // Pass lead alert flag for routing
        alertSessionId: sessionId  // Pass lead alert session ID
      });
      console.log('[TelnyxIntegration] Registered incoming call with phone:', callerPhoneNumber, 'dbCallId:', dbCallId, 'isLeadAlert:', isLeadAlert);
    } catch (error) {
      console.error('[TelnyxIntegration] Failed to register incoming call:', error);
      return;
    }

    // PRIMARY: Use leadInfo from client_state (no API call needed!)
    // Backend passes lead info directly via client_state when dialing WebRTC agent
    if (leadInfo) {
      console.log('[TelnyxIntegration] Using lead info from client_state:', {
        leadId: leadInfo.id,
        leadName: leadInfo.name,
      });

      // Update session with lead info from client_state
      const session = this.sessionService.findByTelnyxId(telnyxCallId);
      if (session) {
        this.sessionService.updateSession(session.id, {
          leadId: leadInfo.id,
          notes: `Caller: ${leadInfo.name}`,
          phoneNumber: callerPhoneNumber,
        });
        console.log('[TelnyxIntegration] Updated session with lead info from client_state');
      }
      return; // No API call needed
    }

    // FALLBACK: Use phone number lookup to find lead info
    console.log('[TelnyxIntegration] Using phone lookup:', callerPhoneNumber);
    this.lookupLeadByPhone(callerPhoneNumber, telnyxCallId);
  }

  /**
   * Look up lead by phone number
   */
  private lookupLeadByPhone(phoneNumber: string, telnyxCallId: string): void {
    this.leadLookupService.lookupByPhoneNumber(phoneNumber).subscribe({
      next: (leadInfo) => {
        if (leadInfo) {
          console.log('[TelnyxIntegration] Lead found by phone:', {
            leadId: leadInfo.id,
            name: leadInfo.fullName,
          });

          const session = this.sessionService.findByTelnyxId(telnyxCallId);
          if (session) {
            this.sessionService.updateSession(session.id, {
              leadId: leadInfo.id,
              notes: `Caller: ${leadInfo.fullName}`
            });
          }
        } else {
          console.log('[TelnyxIntegration] No lead found for phone:', phoneNumber);
        }
      },
      error: (error) => {
        console.error('[TelnyxIntegration] Phone lookup failed:', error);
      }
    });
  }

  private handleCallAnswered(data: any): void {
    const session = this.getCurrentSession();
    if (!session) return;

    // Apply minimum ringback duration logic
    const elapsedTime = Date.now() - session.startTime.getTime();
    if (elapsedTime >= MIN_RINGBACK_DURATION_MS) {
      // Sufficient time has passed, transition immediately
      this.lifecycle.transitionTo('connected', session);
      this.sessionService.updateSession(session.id, { state: 'connected', disposition: 'answered' });
    } else {
      // Wait for minimum duration before transitioning
      setTimeout(() => {
        if (this.lifecycle.currentState() !== 'connected' && this.getCurrentSession()?.id === session.id) {
          this.lifecycle.transitionTo('connected', session);
          this.sessionService.updateSession(session.id, { state: 'connected', disposition: 'answered' });
        }
      }, MIN_RINGBACK_DURATION_MS - elapsedTime);
    }
  }

  private handleCallEnded(data: any): void {
    // Try to get session from currentCall first
    let session = this.getCurrentSession();

    // If no session from currentCall, try to find by call ID from event data
    if (!session && data.call?.id) {
      session = this.sessionService.findByTelnyxId(data.call.id);
    }

    if (session) {
      const disposition = data.disposition || session.disposition || 'no-answer';
      this.lifecycle.terminateCall(session, disposition);
    } else {
      // Last resort: force transition to ended if we're in an active state
      const currentState = this.lifecycle.currentState();
      if (['connected', 'dialing', 'ringing', 'connecting', 'held'].includes(currentState)) {
        this.lifecycle.transitionTo('ended');
      }
    }
  }

  private handleCallTerminal(data: any, disposition: string, notePrefix: string): void {
    const session = this.getCurrentSession();
    if (session) {
      this.sessionService.updateSession(session.id, {
        disposition: disposition as any,
        notes: (session.notes || '') + ` - ${notePrefix}${data.cause || ''}`
      });
      this.lifecycle.transitionTo('failed', session);
    }
  }

  // ============================================================================
  // State Synchronization
  // ============================================================================

  /**
   * Sync application state with Telnyx WebRTC state
   * Based on call-state.service.ts lines 2162-2222
   */
  private syncWithTelnyxState(telnyxState: any, telnyxCall: any): void {
    // Skip sync if disabled
    if (this.preventWebRTCSync || this.isTerminating) return;

    const mappedState = STATE_MAPPING[telnyxState] || 'idle';
    const currentState = this.lifecycle.currentState();

    // Handle terminal states from WebRTC
    if (mappedState === 'ended' || mappedState === 'failed') {
      if (!TERMINAL_STATES.includes(currentState)) this.lifecycle.transitionTo(mappedState);
      return;
    }

    // Don't sync if we're in terminal states unless WebRTC is also terminal
    if (TERMINAL_STATES.includes(currentState) && !['ended', 'failed', 'idle'].includes(mappedState)) return;

    // Sync state if different and transition is valid
    if (mappedState !== currentState && this.lifecycle.canTransitionTo(mappedState)) {
      this.lifecycle.transitionTo(mappedState);
    }
    // Special case: force transition to connected if WebRTC is active
    else if (mappedState === 'connected' && ['dialing', 'ringing', 'connecting'].includes(currentState)) {
      this.lifecycle.transitionTo('connected');
    }
    // Special case: handle WebRTC idle when we have active call session
    else if (mappedState === 'idle') {
      const session = this.getCurrentSession();
      if (session && !['ending', 'ended', 'failed'].includes(currentState)) {
        const callAge = Date.now() - session.startTime.getTime();
        // If call has been active for more than 5 seconds, this might be a real end
        if (callAge > 5000) this.lifecycle.transitionTo('ended');
      }
    }
  }

  /** Handle WebRTC connection loss */
  private handleConnectionLoss(): void {
    if (this.cleanupTimer) clearTimeout(this.cleanupTimer);
    // Wait 3 seconds before cleanup to allow reconnection
    this.cleanupTimer = setTimeout(() => {
      const currentState = this.lifecycle.currentState();
      if (!this.telnyxService.connected() && !TERMINAL_STATES.includes(currentState)) {
        const session = this.getCurrentSession();
        if (session) this.lifecycle.terminateCall(session, 'failed');
      }
    }, 3000);
  }

  // ============================================================================
  // Utilities
  // ============================================================================

  /** Get current active call session from session service */
  private getCurrentSession(): CallSession | null {
    const telnyxCall = this.telnyxService.currentCall();
    return telnyxCall ? this.sessionService.findByTelnyxId(telnyxCall.id) || null : null;
  }

  /** Transition to state if session exists */
  private transitionIfActive(state: CallState): void {
    const session = this.getCurrentSession();
    if (session) this.lifecycle.transitionTo(state, session);
  }

  /** Emit error with consistent format */
  private emitError(code: string, message: string, details: any): void {
    const error = this.errorHandler.processError({ message, details }, code);
    this.eventsService.emitError(error);
  }
}
